import { useRef, useMemo } from "react";
import { getNormalizedColors, linearEase, getStrokeV4 } from "@payment-ui/helpers/Color";
import { getPathProps } from "@payment-ui/helpers/Path";
import { useElapsedTime } from "./useElapsedTime";

interface UseCountDownProps {
    duration: number;
    colors: { colorCode: string; duration: number }[];
    size: number;
    strokeWidth: number;
    rotation: string;
    startAt?: number;
}

interface UseCountdownReturnValue {
    path: string;
    pathLength: number;
    stroke: string;
    strokeDashoffset: number;
    timeProps: {
        remainingTime: number;
        elapsedTime: number;
    };
}

export const useCountdown = (props: UseCountDownProps): UseCountdownReturnValue => {
    const { duration: initDuration, colors, size, strokeWidth, rotation, startAt = 0 } = props;
    const { duration } = useRef({
        duration: initDuration,
    }).current;

    const { path, pathLength } = useMemo(() => getPathProps(size, strokeWidth, rotation), [
        size,
        strokeWidth,
        rotation,
    ]);

    const normalizedColors = useMemo(() => getNormalizedColors(colors, duration), [colors, duration]);
    const { elapsedTime } = useElapsedTime({
        isPlaying: true,
        options: { duration: initDuration, onComplete: (): void => void 0, startAt },
    });

    return {
        path,
        pathLength,
        stroke: getStrokeV4(normalizedColors, elapsedTime),
        strokeDashoffset: linearEase(elapsedTime, 0, pathLength, duration),
        timeProps: {
            remainingTime: Math.ceil(duration - elapsedTime),
            elapsedTime,
        },
    };
};
